/* eslint-disable @typescript-eslint/no-explicit-any */
import * as d3 from "d3";
import { eventEmitter } from "../eventEmitters/EventEmitters";
import { activeSteps, FunnelView, getMode, getView, goalPage, isBuilding, isFunnelEnabled } from "./funnelState";
import { clearMetrics, DateParams, getMetric, getOpenStates, refreshMetrics } from "./funnelMetrics";
import { createMembers } from "./funnelMembers";
import { playbackStates } from "./funnelPlayback";
import { applyBubbles, dimsEffects, dimVisitorNow, litVisitors } from "./funnelSpotlight";
import { advance, START } from "./funnelProgress";
import { ease, laneOf, lengthOf, pointAlong, ridePoints, rideDurationMs, ridesRibbon } from "./funnelTravel";
import { buildFunnelItems, DrawItem, funnelPages, FunnelSlot, nonFunnelKeys, pagesKey, spotlightActive } from "./funnelItems";
import { FUNNEL_BADGE, funnelTooltipHtml, tooltipBackgroundFor } from "./funnelMath";
import "./funnel.scss";

/*
 * Draws the funnel on the dashboard's grid (design of record: docs/FUNNEL_STEP_INDICATORS.md, "CURRENT DIRECTION"): a thick ribbon BEHIND the page boxes from each step's box to the next,
 * narrowing as visitors drop off; a step number in each box's corner; every box that is not part of the funnel dimmed; and a tooltip in the app's own style when a ribbon is hovered.
 * Ribbons and dimming can each be switched off (funnelState.getView). While "Start a new funnel" is picking, the picked boxes are outlined and the grid shows it is waiting for clicks.
 *
 * Deliberately DERIVED, never accumulated: every second it reads the boxes as they are NOW (clusterService hands over a context), works out what should be there, and touches the page only
 * if that differs from what it drew last time. So it needs no hook into every place the layout can change (a resize, a drag, a page assigned to a slot, the zoomed view): whichever happens,
 * the next tick draws the right thing.
 *
 * Imported only behind the build switch (`REACT_APP_DEMO` or `REACT_APP_FUNNEL`, see funnelState.ts and funnelGating.test.ts), so it is not in a production bundle.
 */
export interface FunnelContext {
  svg: any;
  slots: FunnelSlot[];
  boxW: number;
  boxH: number;
  /** the "All other pages" box: not part of any funnel, so it dims with the rest */
  otherKey?: number;
  /** every visitor's bubble right now and the page it is on (or flying to) */
  bubbles?: () => Array<{ id: string; page: string }>;
  /** move one visitor's bubble along a path the funnel plans, given where the bubble is now (clusterService.ts rideBubble) */
  travel?: (visitorId: string, plan: (from: { x: number; y: number }) => { at: (t: number) => { x: number; y: number }; durationMs: number }) => void;
  /** a replayed day is on the grid: the numbers are that whole day's, and who is a funnel visitor comes from `pathOf` (a bubble knows only the page it is on) */
  playback?: boolean;
  /** the pages (in order) a replayed visitor has been shown so far */
  pathOf?: (visitorId: string) => string[] | undefined;
  /** the zoomed view and the single-cluster view have no grid to join boxes on */
  hidden: boolean;
  dateParams: () => DateParams;
}

const TICK_MS = 1000;
const BUBBLE_PASS_MS = 250;
// How often to ask for fresh numbers. THE DEMO asks every second: its in-browser backend counts every visitor still onsite EXACTLY on every call (the ended visits, which only change when
// one ends, are remembered between calls), so a visitor stepping from one page to the next shows up within about a second. It was 30 s, then 5 s, and Chris watched a visitor go from / to /shop
// with the tooltip not moving (2026-10-17). THE REAL BACKEND (staging) is a database read per call (the range's visits plus one pass over their page views), so it is asked every
// REAL_REFRESH_EVERY_TICKS seconds instead; the spotlight does not wait for it (each visitor's place in the funnel is worked out locally, from the pages they step to). If staging shows
// the call costing too much (the "funnel:rpc" line in the build stamp's span list), this is the number to raise, and pushed counts the thing to build (docs/FUNNEL_STEP_INDICATORS.md).
const REAL_REFRESH_EVERY_TICKS = 3;
const REFRESH_EVERY_TICKS = process.env.REACT_APP_DEMO === "1" ? 1 : REAL_REFRESH_EVERY_TICKS;

let provider: (() => FunnelContext | null) | null = null;
let timer = 0;
let bubbleTimer = 0;
let ticks = 0;
let lastSignature = "";
// the pages the numbers were last asked for: when what is needed changes (the goal arrives a moment after the first request after a reload, or a step is added) ask again NOW
let requestedKey = "";
let lastMode = "";
// a replay: the instant the numbers on screen were last asked for (they are asked again whenever the replay's clock, to the minute, moves)
let lastAsOf: number | undefined;
let listening = false;
const lastItems = new Map<string, DrawItem>();
// the ribbon whose tooltip is open right now, so the tooltip can be redrawn when its numbers change
let hoverPage: string | null = null;

const stepPagesNow = () => funnelPages(activeSteps(), goalPage(), isBuilding());
/** Who is in the funnel, by the same rule as the numbers (funnelMembers.ts): the spotlight lights exactly these visitors. */
const members = createMembers(stepPagesNow, getMode);

const refreshNumbers = () => {
  const ctx = provider?.();
  const pages = stepPagesNow();
  if (!ctx || ctx.hidden || pages.length < 2) return;
  refreshMetrics(pages, ctx.dateParams(), getMode());
};

/** What the numbers are about, besides the pages and the mode: in playback, WHICH day (a day does not change while it plays, so it is asked for once, not every second). */
const rangeKey = (ctx: FunnelContext) => (ctx.playback ? ctx.dateParams().startDate : "");

/**
 * Sets the picking class on the grid's root ONLY when it changes. d3's `classed` rewrites the class attribute even when its value is the same, and a write to the root's class can make
 * the browser re-check styles across the whole grid (every bubble): measured as an extra ~55ms long task after every redraw, until this was guarded.
 */
const setPickingClass = (svg: any, on: boolean) => {
  const node = svg?.node?.();
  if (node && node.classList.contains("funnel-picking") !== on) node.classList.toggle("funnel-picking", on);
};

/** Dims exactly the boxes whose slot keys are given (and undims the rest). Touches a box only when its state changes, for the same reason as the picking class. */
const setDim = (svg: any, keys: number[]) => {
  if (!svg) return;
  const want = new Set(keys);
  svg.selectAll(":scope > g[rectIndex]").each(function (this: Element) {
    const on = want.has(Number(this.getAttribute("rectIndex")));
    if (this.classList.contains("funnel-dim") !== on) this.classList.toggle("funnel-dim", on);
  });
};

/**
 * Density clouds (a busy box shows its visitors as a cloud of dots instead of bubbles) are CANVASES the page body holds, one per slot (`canvas[data-density-cloud="<slot>"]`), outside the
 * grid's svg, so the visitor rule above never reaches them (Chris saw the /sale box dimmed with a fully bright cloud over it). A cloud is dimmed whole, when its box is not in the funnel.
 * Clouds come and go as boxes fill and empty, so this runs with the visitors' pass and tags whatever canvases exist at that moment.
 */
const applyClouds = (dimSlots: Set<number> | null) => {
  document.querySelectorAll("canvas[data-density-cloud]").forEach((el) => {
    const dim = !!dimSlots && dimSlots.has(Number(el.getAttribute("data-density-cloud")));
    if (el.classList.contains("funnel-cloud-dim") !== dim) el.classList.toggle("funnel-cloud-dim", dim);
  });
};

const removeLayers = (svg: any) => {
  svg?.select("#funnel-layer").remove();
  svg?.select("#funnel-badges").remove();
  setPickingClass(svg, false);
  setDim(svg, []);
  applyBubbles(svg, null);
  applyClouds(null);
  lastSignature = "";
};

// ── tooltip: the same #tooltip element and classes the other hover tooltips use ─────────────────────────────────────────────
const placeTooltip = (x: number, y: number) => {
  const el = document.getElementById("tooltip");
  if (!el) return;
  el.style.left = `${x + 14}px`;
  el.style.top = `${y - 8}px`;
  el.style.transform = "translateY(-100%)";
  const r = el.getBoundingClientRect();
  if (r.right > window.innerWidth - 8) el.style.left = `${x + 14 - (r.right - (window.innerWidth - 8))}px`;
  if (r.top < 8) {
    el.style.transform = "none";
    el.style.top = `${y + 18}px`;
  }
};
const tipHtml = (item: DrawItem) => funnelTooltipHtml(item.metric, item.step, item.totalSteps);
const showTip = (event: any, item: DrawItem) => {
  hoverPage = item.page;
  d3.select("#tooltip").interrupt();
  d3.select("#tooltip")
    .attr("class", "historical-cluster-tooltip tooltip-v2 funnel-tooltip")
    .html(tipHtml(item))
    .style("opacity", 1)
    .style("background-color", tooltipBackgroundFor(item.colour))
    .style("border", "none");
  placeTooltip(event.layerX ?? event.offsetX, event.layerY ?? event.offsetY);
};
const moveTip = (event: any) => placeTooltip(event.layerX ?? event.offsetX, event.layerY ?? event.offsetY);
const hideTip = () => {
  hoverPage = null;
  d3.select("#tooltip").transition().duration(200).style("opacity", 0);
};
/** The numbers moved while a tooltip was open: redraw it in place (same spot), or close it if its ribbon is gone. */
const refreshOpenTip = (items: DrawItem[]) => {
  if (!hoverPage) return;
  const current = items.find((d) => d.page === hoverPage);
  if (!current) {
    hideTip();

    return;
  }
  d3.select("#tooltip").html(tipHtml(current)).style("background-color", tooltipBackgroundFor(current.colour));
};

// ── drawing ──────────────────────────────────────────────────────────────────────────────────────────────────────────────────
const ensureLayers = (svg: any) => {
  let layer = svg.select("#funnel-layer");
  if (layer.empty()) {
    // Right after the grey background, so it sits UNDER every box group: the ribbons run behind the boxes and the boxes join up, and the page names and counts (which live in those groups)
    // draw over them.
    const back = svg.select("#maincluster").node();
    layer = svg.insert("g", () => (back ? back.nextSibling : null)).attr("id", "funnel-layer");
  }
  let badges = svg.select("#funnel-badges");
  if (badges.empty()) {
    // The step numbers sit INSIDE the boxes, so above them: after the last box group, and (like the flow lines) still below every bubble.
    const boxNodes = svg.selectAll(":scope > g[rectIndex]").nodes();
    const last = boxNodes[boxNodes.length - 1];
    badges = svg.insert("g", () => (last ? last.nextSibling : null)).attr("id", "funnel-badges").style("pointer-events", "none");
  }

  return { layer, badges };
};

const render = (ctx: FunnelContext, items: DrawItem[], badgeItems: Array<{ page: string; step: number; x: number; y: number }>, building: boolean, view: FunnelView, dimKeys: number[]) => {
  const { layer, badges } = ensureLayers(ctx.svg);
  items.forEach((d) => lastItems.set(d.page, d));
  refreshOpenTip(view.ribbons ? items : []);

  const ribbons = layer.selectAll("path.funnel-ribbon").data(view.ribbons ? items : [], (d: any) => d.page);
  ribbons.exit().remove();
  const entered = ribbons.enter().append("path").attr("class", "funnel-ribbon");
  entered
    .merge(ribbons)
    .attr("d", (d: DrawItem) => d.d)
    .attr("fill", (d: DrawItem) => d.colour)
    .attr("fill-opacity", view.byLoss ? 0.62 : 0.4)
    // handlers read the CURRENT item for the page, so a tooltip is never one refresh behind
    .on("mouseenter", (event: any, d: DrawItem) => showTip(event, lastItems.get(d.page) || d))
    .on("mousemove", moveTip)
    .on("mouseleave", hideTip);

  const shownBadges = building || view.ribbons || view.dim ? badgeItems : [];
  const tags = badges.selectAll("g.funnel-badge").data(shownBadges, (d: any) => d.page);
  tags.exit().remove();
  const tagIn = tags.enter().append("g").attr("class", "funnel-badge");
  tagIn.append("rect").attr("class", "funnel-badge-ring").attr("fill", "none").attr("stroke", FUNNEL_BADGE).attr("stroke-width", 2);
  tagIn.append("rect").attr("class", "funnel-badge-bg").attr("rx", 8).attr("height", 16).attr("fill", FUNNEL_BADGE);
  tagIn.append("text").attr("class", "funnel-badge-text").attr("fill", "#fff").attr("font-size", 10).attr("font-weight", 600).attr("dy", 11.5).attr("dx", 7);
  const tagAll = tagIn.merge(tags);
  tagAll.attr("transform", (d: any) => `translate(${d.x},${d.y})`);
  tagAll.select("text.funnel-badge-text").text((d: any) => `Step ${d.step}`);
  tagAll.select("rect.funnel-badge-bg").attr("width", (d: any) => 14 + `Step ${d.step}`.length * 5.4);
  // while picking, every picked box is outlined (the ring is drawn from the badge's corner back out to the box's own corner)
  tagAll
    .select("rect.funnel-badge-ring")
    .style("display", building ? null : "none")
    .attr("x", -6)
    .attr("y", -6)
    .attr("width", ctx.boxW)
    .attr("height", ctx.boxH);
  setPickingClass(ctx.svg, building);
  // the catch-all box dims whenever anything dims (dimKeys is empty when dimming is off, while picking, or with no funnel)
  setDim(ctx.svg, dimKeys.length && ctx.otherKey !== undefined ? [...dimKeys, ctx.otherKey] : dimKeys);
};

/** The visitors' pass: cheap, so it runs several times a second and right after a visitor arrives or moves (a new bubble must not sit lit for a second before it is judged). */
const bubblePass = () => {
  const ctx = provider?.();
  if (!ctx?.svg) return;
  const steps = activeSteps();
  if (!isFunnelEnabled() || ctx.hidden || !ctx.bubbles || !spotlightActive(steps, isBuilding(), getView().dim)) {
    applyBubbles(ctx.svg, null);
    applyClouds(null);
    members.reset();

    return;
  }
  const pages = stepPagesNow();
  const bubbles = ctx.bubbles();
  if (ctx.playback && ctx.pathOf) members.replace(playbackStates(bubbles, ctx.pathOf, pages, getMode()));
  else members.observe(bubbles);
  applyBubbles(ctx.svg, litVisitors(bubbles, members.stateOf, getMode() === "strict", pages));
  applyClouds(new Set(nonFunnelKeys(ctx.slots, pages, ctx.otherKey)));
};
let passTimer = 0;
const schedulePass = () => {
  if (passTimer) return;
  passTimer = window.setTimeout(() => {
    passTimer = 0;
    bubblePass();
  }, 60);
};

const tick = () => {
  ticks++;
  const ctx = provider?.();
  if (!isFunnelEnabled() || !ctx || ctx.hidden) {
    if (ctx?.svg) removeLayers(ctx.svg);
    else lastSignature = "";

    return;
  }
  const building = isBuilding();
  const steps = activeSteps();
  const view = getView();
  const built = buildFunnelItems({ steps, goal: goalPage(), building, slots: ctx.slots, boxW: ctx.boxW, boxH: ctx.boxH, metric: getMetric, byLoss: view.byLoss, dim: view.dim });
  if (!steps.length && !building) {
    if (lastSignature !== "") removeLayers(ctx.svg);

    return;
  }
  // what the numbers are FOR: the pages in order, and Strict or Loose (they mean different things). Either changing asks again at once; otherwise ask on the timer, but not for a hidden tab.
  if (getMode() !== lastMode) {
    lastMode = getMode();
    clearMetrics();   // Strict and Loose numbers answer different questions: never show one under the other's name
  }
  const needed = `${pagesKey(stepPagesNow())}|${getMode()}|${rangeKey(ctx)}`;
  if (needed !== requestedKey) {
    requestedKey = needed;
    lastAsOf = ctx.playback ? ctx.dateParams().asOf : undefined;
    members.reset();   // every visitor's progress means something else now: the next answer re-seeds it
    refreshNumbers();
  } else if (ctx.playback) {
    // a replay's numbers describe an instant of the day: ask again when the clock has moved, not on a timer
    const asOf = ctx.dateParams().asOf;
    if (asOf !== lastAsOf && !document.hidden) {
      lastAsOf = asOf;
      refreshNumbers();
    }
  } else if (ticks % REFRESH_EVERY_TICKS === 0 && !document.hidden) {
    refreshNumbers();
  }
  // the layer can be removed from under us (the grid is rebuilt on a resize): then it must be drawn again whatever the signature says
  const gone = ctx.svg.select("#funnel-layer").empty();
  const signature = `${built.signature}${view.ribbons ? "R" : "r"}`;
  if (signature === lastSignature && !gone) return;
  lastSignature = signature;
  render(ctx, built.items, built.badges, building, view, built.dimKeys);
  bubblePass();
};

const prefersReducedMotion = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (e) {
    return false;
  }
};

/**
 * A visitor has just moved from `from` to `to` (clusterService calls this the moment their bubble's page changes). If they are a funnel visitor stepping from step k to step k+1 and that ribbon
 * is on screen, their bubble RIDES it. Judged against where they were BEFORE this move (the members' state is only advanced by the pass that follows).
 */
export const onVisitorMoved = (id: string, from: string | null | undefined, to: string) => {
  if (!isFunnelEnabled()) return;
  const view = getView();
  if (!view.ride || !view.ribbons || prefersReducedMotion()) return;
  const ctx = provider?.();
  if (!ctx || ctx.hidden || !ctx.travel) return;
  if (!ridesRibbon(from, to, stepPagesNow(), members.stateOf(id), getMode() === "strict")) return;
  const item = from ? lastItems.get(from) : undefined;
  if (!item || item.next !== to) return;
  const along = ridePoints(item.centre, laneOf(id), item.arriveAt);
  ctx.travel(id, (here) => {
    const path = [here, ...along];

    return { at: (t: number) => pointAlong(path, ease(t)), durationMs: rideDurationMs(lengthOf(path)) };
  });
};

/**
 * Should the burst/confetti for this visitor be dimmed? `page` is where they are (or, for an arrival, where they landed). A visitor the funnel has not seen yet is judged by that page alone:
 * landing on step 1 is how a visitor enters. Always false when the spotlight is off.
 */
export const dimsEffectsFor = (id: string, page: string | undefined, leaving = false): boolean => {
  const ctx = provider?.();
  if (!ctx?.svg || !isFunnelEnabled() || ctx.hidden || !spotlightActive(activeSteps(), isBuilding(), getView().dim)) return false;
  const pages = stepPagesNow();
  // a visitor the funnel has not seen yet is judged by where they have landed
  const state = members.stateOf(id) ?? (page ? advance(START, page, pages, getMode()) : undefined);

  return dimsEffects(true, state, getMode() === "strict", page, pages, leaving);
};

/** A visitor is closing: dim all of them now (the dock burst is dimmed separately, by dimsEffectsFor(id, page, true)). */
export const visitorLeaving = (id: string) => {
  const ctx = provider?.();
  if (ctx?.svg) dimVisitorNow(ctx.svg, id);
};

/** Starts drawing. Safe to call again (the provider is replaced, nothing is doubled). Does nothing unless the feature is switched on for this browser. */
export const startFunnelLayer = (next: () => FunnelContext | null) => {
  if (!isFunnelEnabled()) return;
  provider = next;
  if (!listening) {
    listening = true;
    eventEmitter.on("funnelChanged", () => {
      refreshNumbers();
      tick();
    });
    eventEmitter.on("funnelMetricsChanged", () => {
      if (!provider?.()?.playback) members.seed(getOpenStates());   // the backend knows every open visitor's whole path: adopt it, then judge the bubbles again (not in playback: those visitors are today's, the grid shows a replay)
      schedulePass();
      tick();
    });
    eventEmitter.on("newSession", schedulePass);
    eventEmitter.on("sessionChanged", schedulePass);
    eventEmitter.on("filterChanged", () => {
      clearMetrics();   // another date range: the old numbers are for another question
      refreshNumbers();
    });
    eventEmitter.on("modeChanged", () => {
      // live <-> playback, or another day: nothing on screen means the same thing any more (the numbers, who is lit, the drawn ribbons)
      clearMetrics();
      members.reset();
      lastSignature = "";
      requestedKey = "";
      lastAsOf = undefined;
      tick();
    });
  }
  if (!timer) {
    timer = window.setInterval(tick, TICK_MS);
    bubbleTimer = window.setInterval(bubblePass, BUBBLE_PASS_MS);
    refreshNumbers();
    tick();
  }
};

export const stopFunnelLayer = () => {
  if (timer) window.clearInterval(timer);
  timer = 0;
  if (bubbleTimer) window.clearInterval(bubbleTimer);
  bubbleTimer = 0;
  const ctx = provider?.();
  if (ctx?.svg) removeLayers(ctx.svg);
};
